import {
  Box,
  Button,
  Paper,
  Typography,
  Grid,
  CircularProgress,
  useMediaQuery,
} from '@mui/material';
import SelectRebuild from '../../../components/Inputs/SelectRebuild';
import { GROUPBY } from '../../../constants/Reports';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import {
  selectCharltonReport,
  selectCSVCharltonReport,
  selectEmployeeData,
} from '../../../store/ReportsDetailsSlice/selectors';
import { ExportCsvButton } from '../../../components/Buttons/ExportCsvButton';
import { useDateRangeFilter } from '../hooks/useDateRangeFilter';
import { useDispatch } from 'react-redux';
import { FilterPanel } from '../../../components/Reports/FilterPanel';
import { useEffect, useMemo } from 'react';
import moment from 'moment';
import {
  getCharltonReportData,
  getCSVCharltonReportData,
  getEmployeeList,
  setCSVCharltonReportData,
} from '../../../store/ReportsDetailsSlice/actions';
import { saveCsvToFile } from '../../../utils/saveCsvToFile';
import ReusableGrid from '../../../components/Grid/index.jsx';
import { useRowData } from './hooks/useRowData.jsx';
import DataNotFound from '../../../components/Reports/DataNotFound';
import ReportsHeader from '../../../components/Reports/ReportsHeader';

const defaultFormValues = {
  emp_code: [],
  groupby: { id: GROUPBY[0].value, name: GROUPBY[0].title },
};

const CharltonReport = () => {
  const dispatch = useDispatch();
  const { employeeList, loadingEmployeeList } = useSelector(selectEmployeeData);
  const { charltonReportData, isLoadingCharltonReport } =
    useSelector(selectCharltonReport);
  const { charltonCSVReportData, isLoadingCSVCharltonReport } = useSelector(
    selectCSVCharltonReport
  );
  const isTabletVerticalWidth = useMediaQuery('(max-width:1024px)');
  const isTabletWidth = useMediaQuery('(max-width:899px)');

  const { fromDate, toDate, render } = useDateRangeFilter();

  const { register, setValue, watch } = useForm({
    defaultValues: defaultFormValues,
  });
  const { emp_code: employees, groupby } = watch();

  const headers = useMemo(() => {
    if (!charltonReportData.headers) return [];

    return Object.entries(charltonReportData.headers).map(([key, value]) => ({
      headerName: value,
      field: key,
    }));
  }, [charltonReportData.headers]);

  const param = useMemo(() => {
    return {
      start_dt: moment(fromDate).format('YYYY-MM-DD') + ' 00:00:00',
      end_dt: moment(toDate).format('YYYY-MM-DD') + ' 23:59:59',
      sel_order: groupby.id,
      sel_emp: employees.length !== 0 ? employees.map((item) => item.id) : null,
    };
  }, [fromDate, toDate, employees, groupby.id]);

  const handleFindClick = () => dispatch(getCharltonReportData(param));
  const handleDownloadReport = () =>
    dispatch(getCSVCharltonReportData({ ...param, csv: 1 }));

  useEffect(() => {
    if (charltonCSVReportData?.data !== null) {
      saveCsvToFile(charltonCSVReportData.data, charltonCSVReportData.filename);
      dispatch(setCSVCharltonReportData({ data: null, filename: '' }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [charltonCSVReportData]);

  useEffect(
    () => {
      dispatch(getEmployeeList());
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(
    () => {
      dispatch(getCharltonReportData(param));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [groupby]
  );

  const rawFilterConfig = [
    {
      key: 'group_by',
      label: 'Group By',
      show: GROUPBY.length > 0,
      renderComponent: () => (
        <SelectRebuild
          options={GROUPBY.map((item) => ({
            id: item.value,
            name: item.title,
          }))}
          name="groupby"
          label="Group By"
          multiple={false}
          loading={false}
          register={register}
          setValue={setValue}
          watch={watch}
        />
      ),
    },
    {
      key: 'emp_code',
      label: 'Employee',
      show: true,
      renderComponent: () => (
        <SelectRebuild
          options={employeeList || []}
          name="emp_code"
          label="Employee"
          multiple={true}
          allOption={{ id: 'ALL', name: 'All' }}
          loading={loadingEmployeeList}
          register={register}
          setValue={setValue}
          watch={watch}
          enableAllOption={true}
        />
      ),
    },
    {
      key: 'find_button',
      label: '',
      show: true,
      renderComponent: () => (
        <Grid
          item
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            height: '100%',
            paddingTop: '23px',
          }}
        >
          <Button
            variant="contained"
            onClick={handleFindClick}
            sx={{ whiteSpace: 'nowrap', height: '32px' }}
          >
            Find
          </Button>
        </Grid>
      ),
    },
    {
      key: 'export',
      label: '',
      show: true,
      renderComponent: () => (
        <Grid
          item
          sx={{
            width: '100%',
            display: 'flex',
            gap: 2,
            justifyContent: isTabletVerticalWidth ? 'start' : 'end',
            alignItems: 'center',
            height: '100%',
            paddingTop: '23px',
          }}
        >
          <ExportCsvButton
            onClick={handleDownloadReport}
            disabled={isLoadingCSVCharltonReport}
            text={'Export CSV'}
          />
        </Grid>
      ),
    },
  ];

  const filterConfig = rawFilterConfig.filter((f) => f.show !== false);

  const rows = useRowData(charltonReportData.data);

  return (
    <Paper className="p-4" sx={{ marginTop: '1rem' }}>
      <ReportsHeader title="Holiday Time Report Details" />

      <Box className="mt-4" sx={{ width: '100%' }}>
        <Grid container sx={{ width: '100%', mb: '24px' }}>
          <FilterPanel
            filters={filterConfig}
            columns={isTabletVerticalWidth ? 1 : rawFilterConfig.length}
          />
        </Grid>

        <Grid container sx={{ width: '100%', mb: '24px' }}>
          <Grid
            item
            sx={{
              display: 'flex',
              gap: 2,
              width: '100%',
              alignItems: 'center',
            }}
          >
            <Grid
              item
              sx={{
                display: 'flex',
                flexDirection: isTabletWidth ? 'column' : 'row',
                gap: 2,
                width: '100%',
                mt: 'auto',
              }}
            >
              {render}
            </Grid>
          </Grid>
        </Grid>

        <Box>
          {isLoadingCharltonReport ? (
            <Box
              sx={{ minHeight: '100px' }}
              className="w-100 h-100 d-flex justify-content-center align-items-center"
            >
              <CircularProgress />
            </Box>
          ) : rows.length > 0 ? (
            <Box sx={{ display: 'flex', gap: '24px', flexDirection: 'column' }}>
              {rows.map((item) => (
                <Box
                  sx={{ display: 'flex', gap: '20px', flexDirection: 'column' }}
                  key={item.mainTitle}
                >
                  <Typography variant="h5" fontWeight={600}>
                    {item.mainTitle}
                  </Typography>

                  {item.data.map((info) => (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '16px',
                      }}
                      key={`${item.mainTitle}-${info.itemHeader}`}
                    >
                      <Typography variant="h6" fontWeight={500}>
                        {info.itemHeader}
                      </Typography>
                      <ReusableGrid columns={headers} rows={info.info} />
                    </Box>
                  ))}
                </Box>
              ))}
            </Box>
          ) : (
            <DataNotFound />
          )}
        </Box>
      </Box>
    </Paper>
  );
};

export default CharltonReport;
